import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useLang } from '@/lib/i18n';
import SEO from '@/components/SEO';

// A mistyped link can carry percent-encoding or a very long tail; show it
// readably and never let it blow out the layout.
const readablePath = (pathname) => {
    let raw = pathname.replace(/^\/+|\/+$/g, '');
    try {
        raw = decodeURIComponent(raw);
    } catch {
        // malformed escape — show the path as typed
    }
    return raw.length > 80 ? `${raw.slice(0, 77)}…` : raw;
};

// The screen for a URL the app does not have. It is served as a real 404 to
// crawlers, so a dead link is reported as missing instead of indexed.
export default function UnknownRoute() {
    const location = useLocation();
    const { t } = useLang();
    const pageName = readablePath(location.pathname);

    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'prerender-status-code';
        meta.content = '404';
        document.head.appendChild(meta);
        return () => meta.remove();
    }, []);

    const { data: authData, isFetched } = useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            try {
                const user = await base44.auth.me();
                return { user, isAuthenticated: true };
            } catch (error) {
                return { user: null, isAuthenticated: false };
            }
        }
    });

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-[#f7f6f2]">
            <SEO title={t('notFound.page')} titleEs={t('notFound.page')} path={location.pathname} noindex />
            <div className="max-w-md w-full">
                <div className="text-center space-y-6">
                    {/* 404 Error Code */}
                    <div className="space-y-2">
                        <p className="text-7xl font-light text-slate-300" aria-hidden="true">404</p>
                        <div className="h-0.5 w-16 bg-slate-200 mx-auto"></div>
                    </div>

                    {/* Main Message */}
                    <div className="space-y-3">
                        <h1 className="text-2xl font-medium text-slate-800">
                            {t('notFound.page')}
                        </h1>
                        <p className="text-slate-600 leading-relaxed">
                            {pageName
                                ? t('notFound.pageBody', { path: pageName })
                                : t('notFound.pageBodyGeneric')}
                        </p>
                    </div>

                    {/* Admin Note */}
                    {isFetched && authData.isAuthenticated && authData.user?.role === 'admin' && (
                        <div className="mt-8 p-4 bg-slate-100 rounded-lg border border-slate-200">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                                </div>
                                <p className="text-left text-sm text-slate-600 leading-relaxed">
                                    {t('notFound.adminNote')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Action Button */}
                    <div className="pt-6">
                        <Link
                            to="/"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-500"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            {t('notFound.home')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}